<!-- Card Header: Title + Search + CTA -->
<div
  class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-5 border-b border-slate-100"
>
  <h2 class="text-base font-semibold text-slate-800">Workflow Configuration</h2>
  <div class="flex items-center gap-3 flex-1 sm:flex-none sm:justify-end">
    <!-- Search -->
    <div class="relative flex-1 sm:w-72">
      <svg
        class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <button
        type="button"
        (click)="openFilters()"
        class="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 text-left focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all duration-200"
      >
        {{
          activeFilterCount
            ? activeFilterCount +
              " active filter" +
              (activeFilterCount > 1 ? "s" : "")
            : "Search and filter workflows"
        }}
      </button>
    </div>
    <!-- Create Button -->
     @if (authenticationService.hasRoles(["CAN_CREATE_Workflow_Management"])) {
       <button
         (click)="openCreateModal()"
         class="flex items-center gap-2 px-4 py-2 page-btn page-btn-primary active:scale-95 text-white text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
       >
         <svg
           xmlns="http://www.w3.org/2000/svg"
           class="h-4 w-4"
           fill="none"
           viewBox="0 0 24 24"
           stroke="currentColor"
         >
           <path
             stroke-linecap="round"
             stroke-linejoin="round"
             stroke-width="2"
             d="M12 4v16m8-8H4"
           />
         </svg>
         Create Workflow
       </button>
     }
  </div>
</div>

<!-- Table -->
<div class="relative overflow-x-auto">
  <table class="w-full text-sm">
    <thead>
      <tr class="bg-slate-50/70">
        <th
          *ngFor="let col of columns"
          class="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none"
        >
          {{ col }}
        </th>
      </tr>
    </thead>
    <tbody class="divide-y divide-slate-100">
      @if (isLoadingWorkflows) {
        <tr>
          <td colspan="7" class="px-6 py-16 text-center">
            <div class="flex flex-col items-center gap-3">
              <div
                class="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-vms-bg-secondary"
              ></div>
              <p class="text-sm font-medium text-slate-500">Loading workflow...</p>
            </div>
          </td>
        </tr>
      } @else {
        @for (wf of workflowList?.data?.content; track $index; let i = $index) {
          <tr
            class="relative group hover:bg-teal-50/30 transition-colors duration-150 animate-row-in"
            [style.animation-delay]="i * 60 + 'ms'"
            [style.z-index]="workflowList.data.content.length - i"
          >
            <td class="px-6 py-4 font-medium text-slate-800 whitespace-nowrap">
              {{ wf?.name | underscore | titlecase}}
            </td>
            <td class="px-6 py-4 text-slate-500">{{ wf?.description }}</td>
            <td class="px-6 py-4 text-slate-600 whitespace-nowrap">
              {{ wf?.mdaName }}
            </td>
            <td class="px-6 py-4 text-slate-600 text-center">
              <span
                class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-teal-50 text-teal-700 font-semibold text-xs"
              >
                {{ wf?.steps }}
              </span>
            </td>
            <td class="px-6 py-4 text-slate-500 whitespace-nowrap">
              {{ wf?.createdAt | date }}
            </td>
            <td class="px-6 py-4">
              <span
                [class]="getStatusClass(wf.status)"
                class="px-2.5 py-1 rounded-full text-xs font-medium"
              >
                {{ wf?.status }}
              </span>
            </td>
            <td class="px-6 py-4">
              <app-dropdown-menu>
                <button
                  class="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 group-hover:opacity-100 transition-all duration-150 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  trigger
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="5" cy="12" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="19" cy="12" r="1.5" />
                  </svg>
                </button>

                <ng-container content>
                  @if (authenticationService.hasPermissions(["CAN_VIEW_Workflow_Management"])) {
                    <button
                      (click)="onView(wf)"
                      class="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                        <path d="M0 0h24v24H0z" fill="none" />
                        <g fill="none" stroke="currentColor" stroke-width="1.5">
                          <path stroke-linecap="round" d="M9 4.46A9.8 9.8 0 0 1 12 4c4.182 0 7.028 2.5 8.725 4.704C21.575 9.81 22 10.361 22 12c0 1.64-.425 2.191-1.275 3.296C19.028 17.5 16.182 20 12 20s-7.028-2.5-8.725-4.704C2.425 14.192 2 13.639 2 12c0-1.64.425-2.191 1.275-3.296A14.5 14.5 0 0 1 5 6.821" />
                          <path d="M15 12a3 3 0 1 1-6 0a3 3 0 0 1 6 0Z" />
                        </g>
                      </svg>
                      View
                    </button>
                  }

                  @if (authenticationService.hasPermissions(["CAN_EDIT_Workflow_Management"])) {
                    <button
                      (click)="onEdit(wf)"
                      class="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3.5 w-3.5 text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                  }
                  <!-- <button
                    (click)="onToggleStatus(wf)"
                    class="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3.5 w-3.5 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    </svg>
                    {{ wf?.status === "ACTIVE" ? "Deactivate" : "Activate" }}
                  </button> -->
                  <!-- <hr class="my-1 border-slate-100" /> -->

                  @if (authenticationService.hasPermissions(["CAN_DELETE_Workflow_Management"])) {
                    <button
                      (click)="onDelete(i)"
                      class="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete
                    </button>
                  }
                </ng-container>
              </app-dropdown-menu>
            </td>
          </tr>
        } @empty {
          <!-- Empty State -->
          <tr>
            <td colspan="9" class="px-6 py-16 text-center">
              <div class="flex flex-col items-center gap-3">
                <div
                  class="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6 text-slate-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p class="text-sm font-medium text-slate-500">No workflows found</p>
                <p class="text-xs text-slate-400">
                  Try adjusting your search or create a new workflow
                </p>
              </div>
            </td>
          </tr>
        }
      }
    </tbody>
  </table>
</div>

<!-- Table Footer -->
<div
  class="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50"
>
  <p class="text-xs text-slate-400">
    Showing {{ workflowList?.data?.page }} of
    {{ workflowList?.data?.total }} workflows
  </p>
  <div class="flex items-center gap-1">
    <button
      class="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors duration-150 disabled:opacity-40"
    >
      Previous
    </button>
    <button
      class="px-3 py-1.5 text-xs bg-vms-bg-secondary text-vms-text-secondary rounded-md shadow-sm"
    >
      1
    </button>
    <button
      class="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors duration-150"
    >
      Next
    </button>
  </div>
</div>

@if (isModalOpen) {
  <app-workflow-creation-modal
    [isOpen]="true"
    (closed)="isModalOpen = false"
    (workflowSaved)="onWorkflowSaved($event)"
    (workflowUpdated)="fetchWorkflows()"
    [mode]="mode"
    [workflow]="targetWorkflow"
  />
}

<app-filter-drawer
  [isOpen]="isFilterDrawerOpen"
  title="Search Workflows"
  subtitle="Filter workflows by MDA, workflow name, or creation date range."
  submitLabel="Apply"
  resetLabel="Clear"
  [fields]="workflowFilterFields"
  [initialValue]="workflowFilters"
  (close)="closeFilters()"
  (apply)="applyFilters($event)"
></app-filter-drawer>
